import {
    CdkVirtualScrollViewport,
    ScrollingModule,
} from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    Signal,
    ViewChild,
    WritableSignal,
    computed,
    effect,
    signal,
} from '@angular/core';
import { noop } from 'rxjs';
import { DatiqTableModule } from '../../datiq-table.module';
import { IDatiqCellHeaderSort } from '../datiq-cell-header-sort';
import { DatiqTableRowComponent, IDatiqTableRow } from '../datiq-table-row';
import { IqAction } from '../models';
import { DatiqTableSchema, IDatiqTable, emptyDatiqTable } from './models';
import { DatiqTableMapperService, DatiqTableScrollService } from './services';
import { IDatiqCellSelectChange } from '../datiq-cell-select';

@Component({
    selector: 'datiq-table',
    standalone: true,
    imports: [
        CommonModule,
        DatiqTableRowComponent,
        ScrollingModule,
        DatiqTableModule,
    ],
    templateUrl: './datiq-table.component.html',
    styleUrls: ['./datiq-table.component.scss'],
})
export class DatiqTableComponent implements OnInit, AfterViewInit {
    @Input() settings: IDatiqTable = { ...emptyDatiqTable };

    @Input() tableSig: Signal<IDatiqTable>;

    @Output() readonly rowClicked = new EventEmitter<IDatiqTableRow>();

    @Output() readonly scrollInitalized =
        new EventEmitter<DatiqTableScrollService>();

    @Output() readonly sortedColumn = new EventEmitter<IDatiqCellHeaderSort>();

    @Output() readonly actionFired: EventEmitter<IqAction> = new EventEmitter<IqAction>();

    @Output() readonly selectChanged: EventEmitter<IDatiqCellSelectChange> = new EventEmitter<IDatiqCellSelectChange>();

    ds: DatiqTableScrollService;
    pageSize: number;
    itemSize: Signal<number>;

    loaded: Signal<boolean>;
    settingsSig: WritableSignal<IDatiqTable> = signal(this.settings);

    @Output() readonly validate = new EventEmitter<any>();

    private _rowHeight: number = 11;

    @ViewChild(CdkVirtualScrollViewport, { static: false })
    private cdkVirtualScrollViewport: CdkVirtualScrollViewport;

    id: Signal<string> = computed(() => {
        return this.tableSig?.().id;
    });

    height: Signal<number> = computed(() => {
        return this.tableSig?.()?.showAll
        ? (this.tableSig?.()?.rows?.length + 3) * this.itemSize()
        : this.tableSig?.()?.rows?.length < 5
        ? (this.tableSig?.()?.rows?.length + 1) * this.itemSize()
        : (this.tableSig?.()?.pageSize + 2) * this.itemSize();
    });

    headerRow: Signal<IDatiqTableRow> = computed(() => {
        return this.tableSig?.()?.headerRow;
    });

    headerStatic: Signal<boolean> = computed(() => {
        return this.tableSig?.()?.headerStatic ?? false;
    });

    headerRowId: Signal<string> = computed(() => {
        return this.headerRow().id;
    });

    constructor(
        private _mapper: DatiqTableMapperService,
        private injector: Injector,
    ) {
        effect(() => {
            // TODO: Figure out how to make the type for the return work
            const result: any = DatiqTableSchema.safeParse(this.tableSig());

            if (!result.success) {
                // console.error(result.error);
            }
        });
    }

    ngOnInit() {
        this.tableSig = this.tableSig ?? signal(this.settings);
        this.pageSize = this.tableSig()?.pageSize;
        this.itemSize = signal((this.tableSig()?.rowHeight ?? this._rowHeight * 4));
        this.validate.emit(DatiqTableSchema);
        effect(
            () => {
                if (this.tableSig) {
                    this.ds = new DatiqTableScrollService(
                        this.tableSig as WritableSignal<IDatiqTable>,
                        this._mapper,
                        this.cdkVirtualScrollViewport,
                        this.injector,
                        this.loaded as WritableSignal<boolean>,
                    );
                }
            },
            { injector: this.injector },
        );
    }

    ngAfterViewInit() {
        this.scrollInitalized.emit(this.ds);
    }

    sortColumn(headerSort: IDatiqCellHeaderSort): void {
        this.sortedColumn.emit(headerSort);
    }

    handleRowClick(row: IDatiqTableRow): void {
        this.tableSig()?.interactive ? this.rowClicked.emit(row) : noop();
    }
}
